import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Navigate, useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import { setAuth } from '../Redux/authSlice';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../Redux/store';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Spinner from 'react-bootstrap/Spinner';
import './AddStudent.css';

const getCookie = (name: string) => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  if (match) return match[2];
  return null;
};

interface Student {
  name: string;
  date_reg: string;
  guardian_number: string | null;
  nameOf_parent: string | null;
  address: string | null;
  district: string | null;
  Index_number: string;
  gender: string | null;
  image: File | null;
  class_associated: number | null;
  section_associated: number | null;
}

interface Class {
  id: number;
  name: string;
}

interface Section {
  id: number;
  name: string;
  class_associated: number;
}

const AddStudent: React.FC = () => {
  const [student, setStudent] = useState<Student>({
    name: '',
    date_reg: new Date().toISOString().split('T')[0],
    guardian_number: '',
    nameOf_parent: '',
    address: '',
    district: '',
    Index_number: '',
    gender: '',
    image: null,
    class_associated: null,
    section_associated: null,
  });
  const [classes, setClasses] = useState<Class[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const auth = useSelector((state: RootState) => state.auth.value);

  const [added, setAdded] = useState<boolean>(false);
  const [userRole, setUserRole] = useState<string>('');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = getCookie('token');
        if (!token) {
          dispatch(setAuth(false));
          return;
        }
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const { data } = await axios.get('/users/user');
        setUserRole(data.user.role);
        dispatch(setAuth(true));
      } catch (e) {
        console.error('Error fetching user data:', e);
        dispatch(setAuth(false));
      }
    };
    fetchData();
  }, [dispatch]);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const response = await axios.get<any>('/Branches/classes/');
        setClasses(response.data);
      } catch (error) {
        console.error('Error fetching classes:', error);
        console.error('Error fetching classes:', error);
      }
    };
    fetchClasses();
  }, []);

  useEffect(() => {
    const fetchSectionsByClass = async () => {
      if (student.class_associated) {
        try {
          const response = await axios.get<any>(
            `/Branches/sections/?class_id=${student.class_associated}`
          );
          setSections(response.data);
        } catch (error) {
          console.error('Error fetching sections:', error);
        }
      } else {
        setSections([]);
      }
    };
    fetchSectionsByClass();
  }, [student.class_associated]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setStudent({
      ...student,
      [name]: value,
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : null;
    setStudent({
      ...student,
      image: file,
    });
  };

  const handleClassChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const classId = Number(e.target.value);
    setStudent({
      ...student,
      class_associated: classId,
      section_associated: null,
    });
  };

  const handleSectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStudent({
      ...student,
      section_associated: Number(e.target.value),
    });
  };

  const validateForm = () => {
    // Required fields check
    if (!student.name || !student.Index_number || !student.date_reg || !student.nameOf_parent || !student.guardian_number || !student.class_associated || !student.section_associated || !student.gender) {
      toast.error("Fadlan buuxi dhamaan meelaha banaan ee loo baahan yahay *");
      return false;
    }

    // Name validation
    if (student.name.trim().length < 3) {
      toast.error("Magaca ardayga waa inuu ka yaraan 3 xaraf.");
      return false;
    }

    // Parent name validation
    if (student.nameOf_parent.trim().length < 3) {
      toast.error("Magaca masuulka waa inuu ka yaraan 3 xaraf.");
      return false;
    }

    // Phone validation (digits, spaces, dashes allowed, but stripped to digits for length check)
    const phoneDigits = student.guardian_number.replace(/\D/g, '');
    if (phoneDigits.length < 9 || phoneDigits.length > 15) {
      toast.error("Lambarka masuulka waa inuu u dhaxeeyaa 9 ilaa 15 nambar.");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const formData = new FormData();
      Object.keys(student).forEach((key) => {
        const value = student[key as keyof Student];
        if (value !== null && value !== '') {
          formData.append(key, value instanceof File ? value : String(value));
        }
      });
      await axios.post('/Branches/students/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setLoading(false);
      setAdded(true);
      toast.success('Ardayga si guul leh ayaa loo kordhiyay!');
      setTimeout(() => navigate('/students'), 2000);
    } catch (err: any) {
      setLoading(false);
      console.error('Error adding student:', err);

      // Extract detailed error messages from backend response
      let errorMsg = 'Waa la waayay in lagu daro ardayga. Fadlan isku day mar kale.';

      if (err.response?.data) {
        const backendError = err.response.data;

        // Handle field-specific validation errors
        if (typeof backendError === 'object' && !backendError.detail) {
          const fieldErrors: string[] = [];
          Object.keys(backendError).forEach((field) => {
            const messages = Array.isArray(backendError[field])
              ? backendError[field]
              : [backendError[field]];
            fieldErrors.push(`${field}: ${messages.join(', ')}`);
          });
          errorMsg = fieldErrors.length > 0
            ? `Validation errors:\n${fieldErrors.join('\n')}`
            : errorMsg;
        }
        // Handle general error message
        else if (backendError.detail) {
          errorMsg = String(backendError.detail);
        }
        // Handle string error messages
        else if (typeof backendError === 'string') {
          errorMsg = backendError;
        }
      }
      // Handle network errors
      else if (err.message) {
        errorMsg = `Network error: ${err.message}`;
      }
      toast.error(errorMsg, { autoClose: 5000 });

    }


  }


  if (added) {
    return <Navigate to="/students" />;
  }

  // Prevent teachers from accessing this page
  if (auth && userRole === 'teacher') {
    return (
      <div className="dashboard-content">
        <div className="alert alert-warning text-center mt-5 py-4">
          <i className="bi bi-shield-exclamation fs-1 text-warning mb-3 d-block"></i>
          <h5 className="mb-2">Helitaanka Waa La Xadiday</h5>
          <p className="mb-3">Macalimiintu ma haystaan ogolaansho ay ku daraan arday cusub.</p>
          <button className="btn btn-primary" onClick={() => navigate('/students')}>
            Ku Noqo Ardayda
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-content">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="colored" />
      {auth ? (
        <div className="container-fluid px-0">
          {/* Header */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-5">
            <div>
              <h1 className="fw-bold text-dark mb-2">Diiwaangelinta Arday Cusub</h1>
              <p className="text-muted fs-5 mb-0">Diiwaangeli arday cusub nidaamka.</p>
            </div>
            <button className="btn btn-outline-secondary btn-lg rounded-pill px-5" onClick={() => navigate('/students')}>
              Iska Daay
            </button>
          </div>

          <div className="card-modern overflow-hidden">
            <div className="bg-primary bg-opacity-10 px-5 py-4 border-bottom border-primary border-opacity-10">
              <h4 className="text-primary fw-bold mb-0">Foomka Diiwaangelinta</h4>
            </div>
            <div className="card-body p-5">
              <form onSubmit={handleSubmit} className="row g-5">

                {/* Student Personal Info */}
                <div className="col-12">
                  <h5 className="text-dark fw-bold border-bottom pb-3 mb-4">
                    <i className="bi bi-person-badge me-2 text-primary"></i>Xogta Shakhsiga
                  </h5>
                  <div className="row g-4">
                    <div className="col-md-3">
                      <label htmlFor="Index_number" className="form-label fw-bold text-secondary">Lambarka Tixraaca <span className="text-danger">*</span></label>
                      <input type="text" className="form-control form-control-lg bg-light border-0" id="Index_number" name="Index_number" value={student.Index_number} onChange={handleChange} required placeholder="e.g. STD-001" />
                    </div>
                    <div className="col-md-5">
                      <label htmlFor="name" className="form-label fw-bold text-secondary">Magaca Oo Buuxa <span className="text-danger">*</span></label>
                      <input type="text" className="form-control form-control-lg bg-light border-0" id="name" name="name" value={student.name} onChange={handleChange} required placeholder="Magaca buuxa ee ardayga" />
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="gender" className="form-label fw-bold text-secondary">Jinsiga <span className="text-danger">*</span></label>
                      <select className="form-select form-select-lg bg-light border-0" id="gender" name="gender" value={student.gender || ''} onChange={handleChange} required>
                        <option value="">Dooro Jinsiga</option>
                        <option value="Male">Lab</option>
                        <option value="Female">Dheddig</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="date_reg" className="form-label fw-bold text-secondary">Taariikhda Diiwaangelinta <span className="text-danger">*</span></label>
                      <input type="date" className="form-control form-control-lg bg-light border-0" id="date_reg" name="date_reg" value={student.date_reg} onChange={handleChange} required />
                    </div>
                    <div className="col-md-8">
                      <label htmlFor="image" className="form-label fw-bold text-secondary">Sawirka Ardayga</label>
                      <input type="file" className="form-control form-control-lg bg-light border-0" id="image" name="image" onChange={handleFileChange} accept="image/*" />
                    </div>
                  </div>
                </div>

                {/* Academic Details */}
                <div className="col-12">
                  <h5 className="text-dark fw-bold border-bottom pb-3 mb-4">
                    <i className="bi bi-mortarboard me-2 text-primary"></i>Faahfaahinta Tacliinta
                  </h5>
                  <div className="row g-4">
                    <div className="col-md-6">
                      <label htmlFor="class_associated" className="form-label fw-bold text-secondary">Fasalka <span className="text-danger">*</span></label>
                      <select className="form-select form-select-lg bg-light border-0" id="class_associated" name="class_associated" value={student.class_associated !== null ? student.class_associated : ''} onChange={handleClassChange} required>
                        <option value="">Dooro Fasalka</option>
                        {classes.map((cls) => (
                          <option key={cls.id} value={cls.id}>{cls.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="section_associated" className="form-label fw-bold text-secondary">Qeybta/Fasalka <span className="text-danger">*</span></label>
                      <select className="form-select form-select-lg bg-light border-0" id="section_associated" name="section_associated" value={student.section_associated !== null ? student.section_associated : ''} onChange={handleSectionChange} required disabled={!student.class_associated}>
                        <option value="">Dooro Qeybta</option>
                        {sections.map((sec) => (
                          <option key={sec.id} value={sec.id}>{sec.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Guardian Details */}
                <div className="col-12">
                  <h5 className="text-dark fw-bold border-bottom pb-3 mb-4">
                    <i className="bi bi-people me-2 text-primary"></i>Xogta Masuulka
                  </h5>
                  <div className="row g-4">
                    <div className="col-md-6">
                      <label htmlFor="nameOf_parent" className="form-label fw-bold text-secondary">Magaca Waalidka/Masuulka <span className="text-danger">*</span></label>
                      <input type="text" className="form-control form-control-lg bg-light border-0" id="nameOf_parent" name="nameOf_parent" value={student.nameOf_parent || ''} onChange={handleChange} required placeholder="Magaca Waalidka" />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="guardian_number" className="form-label fw-bold text-secondary">Lambarka Taleefanka <span className="text-danger">*</span></label>
                      <input type="text" className="form-control form-control-lg bg-light border-0" id="guardian_number" name="guardian_number" value={student.guardian_number || ''} onChange={handleChange} required placeholder="e.g. 0615555555" />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="address" className="form-label fw-bold text-secondary">Cinwaanka</label>
                      <input type="text" className="form-control form-control-lg bg-light border-0" id="address" name="address" value={student.address || ''} onChange={handleChange} placeholder="Cinwaanka Deegaanka" />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="district" className="form-label fw-bold text-secondary">Degmada</label>
                      <input type="text" className="form-control form-control-lg bg-light border-0" id="district" name="district" value={student.district || ''} onChange={handleChange} placeholder="Degmada" />
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="col-12 mt-5 border-top pt-4 text-end">
                  <button type="submit" className="btn btn-primary btn-lg px-5 rounded-pill shadow-lg hover-scale" disabled={loading}>
                    {loading ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                        <span className="ms-2">Howsha way socotaa...</span>
                      </>
                    ) : 'Dhameystir Diiwaangelinta'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="mt-3 text-muted">Hubinaya...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddStudent;
